/**
 * Kassie Character Engine (V3)
 * Core SVG manipulation for the Kassie avatar — layer toggling, arm rigging,
 * eye tracking, expressions, and speech bubble control.
 *
 * Used by:
 *   - child-app loader (kassieLoaderV3.js)
 *   - frontend presenter (kassiePresenter.js)
 *
 * The SVG (kassieLoader.svg) has named layers:
 *   Mouths: default, smile-1, smile-2, kiss, m, m-alt, half-smile,
 *           half-smile-flipped, ooh, th-or-v-shape, L, tongue-out, BigSmile
 *   Ears:   left-ear, left-ear-up, right-ear, right-ear-up
 *   Eyes:   left-eye > open > (eye-white-left, left-pupil), left-eye-closed
 *           right-eye > open > (eye-white-right, right-pupil), right-eye-closed
 *           eye-sockets-no-holes, eye-sockets-with-holes (pupil mask)
 *   Arms:   right-arm > (right-arm-front [forearm], right-arm-shoulder-pivot)
 *           left-arm > (left-arm-front [forearm], left-arm-shoulder-pivot)
 *   Special: kassandra-eye-whiskers, kassandra-kiss, stars
 *   Pivots: *-shoulder-pivot, *-elbow-pivot (hidden at runtime)
 */

import { t } from '../i18n/config.js';

// ═══════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════

const CONFIG = {
  svgPath: '/svg/components/kassieLoader-paths.svg',
  eyeUpdateThrottle: 50,
  blinkIntervalMin: 2000,
  blinkIntervalMax: 4000,
  blinkDuration: 300,
  mouthCycleMin: 2000,
  mouthCycleMax: 4000,
};

// Pivot points (absolute coords in viewBox — arms rigged horizontal at 0°)
const PIVOTS = {
  leftShoulder: { x: 40.87, y: 51.74 },
  leftElbow: { x: 62.97, y: 51.61 },
  rightShoulder: { x: 22.37, y: 51.67 },
  rightElbow: { x: -0.14, y: 51.73 },
};

// Mouth layer IDs (only one visible at a time)
const MOUTH_LAYERS = [
  'default', 'smile-1', 'smile-2', 'kiss', 'm', 'm-alt',
  'half-smile', 'half-smile-flipped', 'ooh', 'th-or-v-shape',
  'L', 'tongue-out', 'BigSmile', 'w'
];

// First-letter → mouth-shape overrides used by speak() to pick a held
// shape for a whole word, rather than the random talkFrame() flapping.
// Any letter not listed here falls through to the existing random cycle.
const WORD_START_MOUTH = {
  a: 'ooh', o: 'ooh',
  l: 'L', t: 'L', d: 'L',
  w: 'w', v: 'w', s: 'w', c: 'w',
  m: 'm', n: 'm',
};

// Viseme mapping: phoneme groups → mouth layer for lipsync
const VISEME_MAP = {
  rest: 'smile-1',
  aa: 'default',
  oh: 'ooh',
  ee: 'smile-2',
  mbp: 'm',
  fv: 'th-or-v-shape',
  ltn: 'L',
};

// Layers to always hide (pivot markers)
const HIDDEN_LAYERS = [
  'right-arm-shoulder-pivot', 'right-arm-elbow-pivot',
  'left-arm-shoulder-pivot', 'left-arm-elbow-pivot',
];

// ═══════════════════════════════════════════════════════════════
// CHARACTER INSTANCE CLASS
// ═══════════════════════════════════════════════════════════════

/**
 * KassieCharacter — a single instance of the animated Kassie avatar.
 */
export class KassieCharacter {
  constructor() {
    this.container = null;
    this.svg = null;
    this.layers = {};
    this.currentMouth = 'ooh';
    this.blinking = false;
    this.timers = {};
    this.armsVisible = false;
    this.initialized = false;
  }

  /**
   * Initialize the character by fetching the SVG and caching layer references.
   * @param {HTMLElement} parentEl - Element to append the SVG container to
   * @param {object} [options] - { size: '180px', className: '' }
   */
  async init(parentEl, options = {}) {
    if (this.initialized) return;

    const size = options.size || '180px';
    const className = options.className || '';

    this.container = document.createElement('div');
    this.container.className = `kassie-character ${className}`.trim();
    this.container.style.width = size;
    this.container.style.height = size;
    this.container.setAttribute('aria-hidden', 'true');

    try {
      const resp = await fetch(CONFIG.svgPath);
      const svgText = await resp.text();
      this.container.innerHTML = svgText;
    } catch (err) {
      console.error('Failed to load Kassie SVG:', err);
      return;
    }

    this.svg = this.container.querySelector('svg');
    if (!this.svg) return;

    // Preserve the SVG's native coordinate system for gradient rendering
    // Use CSS to scale while keeping viewBox intact
    this.svg.style.width = '100%';
    this.svg.style.height = '100%';
    this.svg.style.overflow = 'visible';

    // Cache all named layers
    this._cacheLayers();

    // Hide pivot markers
    HIDDEN_LAYERS.forEach(id => this._hideLayer(id));

    // Hide easter-egg layers
    this._hideLayer('kassandra-eye-whiskers');
    this._hideLayer('kassandra-kiss');

    // Hide closed-eye layers (shown only during blink/wink)
    this._hideLayer('left-eye-closed');
    this._hideLayer('right-eye-closed');

    // Hide dizzy-eye layers (shown only during dizzy easter egg)
    this._hideLayer('left-eye-dizzy');
    this._hideLayer('right-eye-dizzy');

    // Hide arms by default
    this._hideLayer('right-arm');
    this._hideLayer('left-arm');

    // Set up arm rigging
    this._setupArmRigging();
    this.armsVisible = false;

    // Set default mouth
    this.setMouth('BigSmile');

    parentEl.appendChild(this.container);
    this.initialized = true;
  }

  // ─── Layer Management ──────────────────────────────────────────────────────

  _cacheLayers() {
    // Iterate all g elements and cache by inkscape:label
    // (CSS selector with colon in attribute name is unreliable across browsers)
    const allGroups = this.svg.querySelectorAll('g');
    allGroups.forEach(g => {
      const label = g.getAttribute('inkscape:label');
      if (label) this.layers[label] = g;
      const id = g.getAttribute('id');
      if (id && !this.layers[id]) this.layers[id] = g;
    });
  }

  _showLayer(name) {
    const el = this.layers[name];
    if (el) el.style.display = 'inline';
  }

  _hideLayer(name) {
    const el = this.layers[name];
    if (el) el.style.display = 'none';
  }

  _isLayerVisible(name) {
    const el = this.layers[name];
    return el && el.style.display !== 'none';
  }

  /** Public layer visibility control */
  showLayer(name) { this._showLayer(name); }
  hideLayer(name) { this._hideLayer(name); }

  /** Remove clip-path from all children of a layer */
  _removeClipPaths(layerName) {
    const el = this.layers[layerName];
    if (!el) return;
    el.querySelectorAll('[clip-path]').forEach(node => node.removeAttribute('clip-path'));
    if (el.getAttribute('clip-path')) el.removeAttribute('clip-path');
  }

  // ─── Mouth Expressions ─────────────────────────────────────────────────────

  /**
   * Set the mouth expression. Hides all mouth layers, shows the specified one.
   * @param {string} mouthId - One of MOUTH_LAYERS
   */
  setMouth(mouthId) {
    MOUTH_LAYERS.forEach(id => this._hideLayer(id));
    // Also hide kassandra-kiss (only visible during kassandra easter egg)
    this._hideLayer('kassandra-kiss');
    this._showLayer(mouthId);
    this.currentMouth = mouthId;
  }

  /** Cycle to a random mouth shape (used when there's no word to anchor to). */
  talkFrame() {
    const talkSequence = [
      'default', 'ooh', 'smile-1', 'default',
      'm', 'ooh', 'th-or-v-shape', 'L', 'w', 'default'
    ];
    const idx = Math.floor(Math.random() * talkSequence.length);
    this.setMouth(talkSequence[idx]);
  }

  /**
   * Set the mouth to the shape associated with a word's first letter (see
   * WORD_START_MOUTH), falling back to a random talkFrame() shape for
   * words that don't start with one of the mapped letters — so every word
   * still gets *some* mouth movement, just not a specifically chosen one.
   * @param {string} word
   */
  setMouthForWord(word) {
    const firstLetter = (word || '').trim().charAt(0).toLowerCase();
    const mouthId = WORD_START_MOUTH[firstLetter];
    if (mouthId) {
      this.setMouth(mouthId);
    } else {
      this.talkFrame();
    }
  }

  /**
   * Set mouth to a specific viseme (for lipsync).
   * @param {'rest'|'aa'|'oh'|'ee'|'mbp'|'fv'|'ltn'} viseme
   */
  setViseme(viseme) {
    const mouthId = VISEME_MAP[viseme] || VISEME_MAP.rest;
    this.setMouth(mouthId);
  }

  // ─── Ears ──────────────────────────────────────────────────────────────────

  setEars(position) {
    if (position === 'up') {
      this._showLayer('left-ear-up');
      this._showLayer('right-ear-up');
      this._hideLayer('left-ear');
      this._hideLayer('right-ear');
    } else {
      this._hideLayer('left-ear-up');
      this._hideLayer('right-ear-up');
      this._showLayer('left-ear');
      this._showLayer('right-ear');
    }
  }

  /**
   * A quick ear tickle/wiggle — the ear rotates from its own base a couple
   * of times in quick succession, then settles back. Ported from the same
   * "quick twitch" motion kassieLoaderV3.js's idle animation already uses
   * (see startEarTwitchCycle() there), as a proper KassieCharacter method
   * so the presenter can trigger it too, rather than duplicating the
   * inline style logic in a second place.
   * @param {'left'|'right'} [side] - defaults to a random ear if omitted
   */
  async earWiggle(side) {
    const pickedSide = side || (Math.random() < 0.5 ? 'left' : 'right');
    const earUp = this.layers[`${pickedSide}-ear-up`];
    if (!earUp) return;

    earUp.style.transition = 'transform 0.15s ease';
    earUp.style.transformOrigin = pickedSide === 'left' ? 'right bottom' : 'left bottom';
    earUp.style.transform = `rotate(${pickedSide === 'left' ? '-2' : '2'}deg) scaleX(1.05)`;
    await this._delay(300);

    earUp.style.transform = 'rotate(0deg) scaleX(1)';
    await this._delay(200);

    // Second, smaller wiggle for good measure — matches the loader's own
    // 50%-chance follow-up twitch.
    earUp.style.transform = `rotate(${pickedSide === 'left' ? '-1.5' : '1.5'}deg) scaleX(1.03)`;
    await this._delay(150);
    earUp.style.transform = 'rotate(0deg) scaleX(1)';
  }

  // ─── Eyebrows ──────────────────────────────────────────────────────────────

  setEyebrows(expression) {
    const brows = this.layers['kassie-body-eyebrows'];
    if (!brows) return;
    switch (expression) {
      case 'raised':
        brows.style.transform = 'translateY(-1.5px)';
        break;
      case 'furrowed':
        brows.style.transform = 'translateY(0.5px) scaleY(0.9)';
        break;
      default:
        brows.style.transform = '';
    }
  }

  /**
   * Eyebrow waggle/flash — a quick raise-lower-raise-lower in succession
   * (the social "eyebrow flash" gesture: a fast double-pump, not a single
   * slow raise). Restores whatever eyebrow state was set before the call
   * once finished, so it can be layered on top of setEyebrows('raised') etc.
   * without fighting it afterward.
   * @param {number} [pulses=2] - number of up/down pulses
   * @param {number} [pulseMs=140] - how long each up or down half-pulse holds
   */
  async eyebrowWaggle(pulses = 2, pulseMs = 140) {
    const brows = this.layers['kassie-body-eyebrows'];
    if (!brows) return;
    const restoreTransform = brows.style.transform;

    for (let i = 0; i < pulses; i++) {
      this.setEyebrows('raised');
      await this._delay(pulseMs);
      brows.style.transform = restoreTransform;
      await this._delay(pulseMs);
    }
  }

  // ─── Eye Tracking ──────────────────────────────────────────────────────────

  lookAt(targetX, targetY) {
    if (this.blinking || !this.svg) return;

    const rect = this.svg.getBoundingClientRect();
    const svgWidth = rect.width;
    const svgHeight = rect.height;

    // Nose position (center of face) in viewport coords
    const noseScreen = {
      x: rect.left + svgWidth * 0.50,
      y: rect.top + svgHeight * 0.42
    };

    // Each eye's screen position
    const leftEyeScreen = {
      x: rect.left + svgWidth * 0.42,
      y: rect.top + svgHeight * 0.36
    };
    const rightEyeScreen = {
      x: rect.left + svgWidth * 0.58,
      y: rect.top + svgHeight * 0.36
    };

    // Distance from cursor to nose
    const distToNose = Math.sqrt(
      (targetX - noseScreen.x) ** 2 + (targetY - noseScreen.y) ** 2
    );

    const maxOffset = 1.2; // SVG units of pupil travel — clamped to stay within sockets

    // Within 50px of nose: each eye tracks cursor independently (cross-eyed effect)
    // Use larger offset for visible convergence
    let leftOffset, rightOffset;

    if (distToNose < 50) {
      // Independent tracking — each eye looks at cursor from its own position
      // Use a bigger offset so convergence is clearly visible
      leftOffset = this._calcPupilOffset(targetX, targetY, leftEyeScreen, 2.0);
      rightOffset = this._calcPupilOffset(targetX, targetY, rightEyeScreen, 2.0);
    } else {
      // Unified tracking — both eyes look in the same direction
      const centerEye = {
        x: (leftEyeScreen.x + rightEyeScreen.x) / 2,
        y: (leftEyeScreen.y + rightEyeScreen.y) / 2
      };
      const offset = this._calcPupilOffset(targetX, targetY, centerEye, maxOffset);
      leftOffset = offset;
      rightOffset = offset;
    }

    // Apply transforms to the pupil layer groups directly
    const leftPupil = this.layers['left-pupil'];
    const rightPupil = this.layers['right-pupil'];
    if (leftPupil) leftPupil.style.transform = `translate(${leftOffset.x}px, ${leftOffset.y}px)`;
    if (rightPupil) rightPupil.style.transform = `translate(${rightOffset.x}px, ${rightOffset.y}px)`;
  }

  _calcPupilOffset(targetX, targetY, eyeCenter, maxOffset) {
    const dx = targetX - eyeCenter.x;
    const dy = targetY - eyeCenter.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const scale = Math.min(1, dist / 150);
    const offsetX = (dx / dist) * maxOffset * scale;
    const offsetY = (dy / dist) * maxOffset * scale;
    return { x: offsetX, y: offsetY };
  }

  lookCenter() {
    const leftPupil = this.layers['left-pupil'];
    const rightPupil = this.layers['right-pupil'];
    if (leftPupil) leftPupil.style.transform = '';
    if (rightPupil) rightPupil.style.transform = '';
  }

  // ─── Blinking & Winking ─────────────────────────────────────────────────────

  blink() {
    if (this.blinking) return;
    this.blinking = true;

    // Hide open eye content by hiding the 'open' sub-layers inside each eye
    this._hideLayer('eye-white-left');
    this._hideLayer('eye-white-right');
    this._hideLayer('left-pupil');
    this._hideLayer('right-pupil');
    this._hideLayer('eye-sockets-with-holes');

    // Show closed eyes — find ALL 'closed' parent groups inside both eye groups
    // and show them, because this.layers['closed'] only stores ONE (last found)
    const leftEye = this.layers['left-eye'];
    const rightEye = this.layers['right-eye'];
    if (leftEye) {
      const closedGroup = leftEye.querySelector('g[inkscape\\:label="closed"]');
      if (closedGroup) closedGroup.style.display = 'inline';
    }
    if (rightEye) {
      const closedGroup = rightEye.querySelector('g[inkscape\\:label="closed"]');
      if (closedGroup) closedGroup.style.display = 'inline';
    }
    this._showLayer('left-eye-closed');
    this._showLayer('right-eye-closed');

    setTimeout(() => {
      this._restoreOpenEyes();
      this.blinking = false;
    }, CONFIG.blinkDuration);
  }

  wink(side = 'right') {
    if (this.blinking) return;
    this.blinking = true;

    // Hide the winking eye's open content
    this._hideLayer(`${side}-pupil`);
    this._hideLayer(`eye-white-${side}`);

    // Show the closed layer for that eye
    // Need to find and show the 'closed' parent inside that eye group
    const eyeGroup = this.layers[`${side}-eye`];
    if (eyeGroup) {
      const closedGroup = eyeGroup.querySelector('g[inkscape\\:label="closed"]');
      if (closedGroup) closedGroup.style.display = '';
    }
    this._showLayer(`${side}-eye-closed`);

    setTimeout(() => {
      this._hideLayer(`${side}-eye-closed`);
      // Re-hide the closed parent
      if (eyeGroup) {
        const closedGroup = eyeGroup.querySelector('g[inkscape\\:label="closed"]');
        if (closedGroup) closedGroup.style.display = 'none';
      }
      this._showLayer(`${side}-pupil`);
      this._showLayer(`eye-white-${side}`);
      this.blinking = false;
    }, CONFIG.blinkDuration * 2);
  }

  _restoreOpenEyes() {
    // Hide all closed-eye layers
    this._hideLayer('left-eye-closed');
    this._hideLayer('right-eye-closed');

    // Re-hide the 'closed' parent groups inside each eye
    const leftEye = this.layers['left-eye'];
    const rightEye = this.layers['right-eye'];
    if (leftEye) {
      const c = leftEye.querySelector('g[inkscape\\:label="closed"]');
      if (c) c.style.display = 'none';
    }
    if (rightEye) {
      const c = rightEye.querySelector('g[inkscape\\:label="closed"]');
      if (c) c.style.display = 'none';
    }

    // Show open eye layers
    this._showLayer('left-pupil');
    this._showLayer('right-pupil');
    this._showLayer('eye-white-left');
    this._showLayer('eye-white-right');
    this._showLayer('eye-sockets-with-holes');
  }

  startBlinking() {
    const scheduleNext = () => {
      const delay = CONFIG.blinkIntervalMin + Math.random() * (CONFIG.blinkIntervalMax - CONFIG.blinkIntervalMin);
      this.timers.blink = setTimeout(() => {
        if (Math.random() < 0.05) {
          this.wink(Math.random() < 0.5 ? 'left' : 'right');
        } else {
          this.blink();
        }
        scheduleNext();
      }, delay);
    };
    scheduleNext();
  }

  stopBlinking() {
    clearTimeout(this.timers.blink);
  }

  // ─── Arm Rigging ───────────────────────────────────────────────────────────

  _setupArmRigging() {
    const rightArm = this.layers['right-arm'];
    const leftArm = this.layers['left-arm'];

    if (rightArm) {
      rightArm.style.transformOrigin = '22.435px 50.561px';
      rightArm.style.transformBox = 'view-box';
      rightArm.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
    }
    if (leftArm) {
      leftArm.style.transformOrigin = '40.868px 50.561px';
      leftArm.style.transformBox = 'view-box';
      leftArm.style.transition = 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)';
    }

    // NOTE: Forearm transform-origin is NOT set here.
    // It's only set dynamically in setForearmRotation() when needed.
    // If set here, it causes the hand to disconnect when the parent arm rotates.
  }

  showArms() {
    if (this.svg) this.svg.style.overflow = 'visible';
    if (this.container) this.container.style.overflow = 'visible';
    if (this.container?.parentElement) this.container.parentElement.style.overflow = 'visible';
    this._showLayer('right-arm');
    this._showLayer('left-arm');
    this._removeClipPaths('right-arm');
    this._removeClipPaths('left-arm');
    this.armsVisible = true;
  }

  hideArms() {
    const rightArm = this.layers['right-arm'];
    const leftArm = this.layers['left-arm'];
    if (rightArm) rightArm.style.transform = '';
    if (leftArm) leftArm.style.transform = '';
    // Reset forearms
    const rightFore = this.layers['right-arm-front'];
    const leftFore = this.layers['left-arm-front'];
    if (rightFore) { rightFore.style.transform = ''; rightFore.style.transformOrigin = ''; rightFore.style.transformBox = ''; }
    if (leftFore) { leftFore.style.transform = ''; leftFore.style.transformOrigin = ''; leftFore.style.transformBox = ''; }
    // Also reset hand transforms
    const rightHand = this.layers['right-hand'];
    const leftHand = this.layers['left-hand'];
    if (rightHand) { rightHand.style.transform = ''; rightHand.style.transformOrigin = ''; rightHand.style.transformBox = ''; }
    if (leftHand) { leftHand.style.transform = ''; leftHand.style.transformOrigin = ''; leftHand.style.transformBox = ''; }
    this._hideLayer('right-arm');
    this._hideLayer('left-arm');
    if (this.svg) this.svg.style.overflow = '';
    if (this.container) this.container.style.overflow = '';
    if (this.container?.parentElement) this.container.parentElement.style.overflow = '';
    this.armsVisible = false;
  }

  /**
   * Rotate an entire arm (upper + forearm follows as child) from the shoulder.
   * @param {'left'|'right'} side
   * @param {number} deg - Negative = up for left arm, Positive = up for right arm
   */
  setArmRotation(side, deg) {
    const armLayer = side === 'left' ? this.layers['left-arm'] : this.layers['right-arm'];
    if (armLayer) armLayer.style.transform = `rotate(${deg}deg)`;
  }

  /**
   * Rotate the forearm from its center point.
   * Uses fill-box + transform-origin: center to keep it anchored.
   * @param {'left'|'right'} side
   * @param {number} deg
   */
  setForearmRotation(side, deg) {
    const fore = side === 'left' ? this.layers['left-arm-front'] : this.layers['right-arm-front'];
    if (!fore) return;
    fore.style.transformBox = 'fill-box';
    // Elbow is where forearm connects to upper arm:
    // Right arm: elbow is at the right edge (closest to body)
    // Left arm: elbow is at the left edge (closest to body)
    fore.style.transformOrigin = side === 'left' ? '7% 46%' : '89% 42%';
    fore.style.transition = 'transform 0.3s ease-in-out';
    fore.style.transform = deg === 0 ? '' : `rotate(${deg}deg)`;
  }

  // ─── Hand Gestures ─────────────────────────────────────────────────────────

  /**
   * Set hand pose. Shows one hand state, hides others.
   * @param {'left'|'right'} side
   * @param {'open'|'clap'|'flat'} pose
   */
  /**
   * Set hand pose. Shows one hand state, hides others.
   * @param {'left'|'right'} side
   * @param {'open'|'clap'|'flat-palm-up'|'flat-palm-down'} pose
   */
  setHand(side, pose) {
    // All hand layers are now uniquely named: right-hand-open, left-hand-clap, etc.
    const prefix = side;
    const allPoses = ['open', 'clap', 'flat-palm-up', 'flat-palm-down', 'flat-palm-down-tilt'];
    allPoses.forEach(p => {
      const name = `${prefix}-hand-${p}`;
      console.log(`setHand: hiding ${name}, found=${!!this.layers[name]}`);
      this._hideLayer(name);
    });
    const showName = `${prefix}-hand-${pose}`;
    console.log(`setHand: showing ${showName}, found=${!!this.layers[showName]}`);
    this._showLayer(showName);
  }

  /**
   * Auto-select the correct hand pose based on IK rules.
   * Uses the global angle (upper arm + forearm rotation) to determine
   * which hand frame looks anatomically correct.
   *
   * Rules:
   *   - Default/neutral: hand-open (70% of cases, conversational idle)
   *   - Palm-down: θ_global between -45° and 45° from horizontal (reaching out/pressing)
   *   - Palm-up: elbow sharply bent (60-130°) and hand above waist (shrug/asking)
   *   - Clap: both hands converging toward center (catching/clapping)
   *
   * @param {'left'|'right'} side
   * @param {number} upperArmAngle - θ₁ in degrees (0 = horizontal)
   * @param {number} forearmAngle - θ₂ in degrees (0 = aligned with upper arm)
   * @param {boolean} [isConverging=false] - Are both hands moving toward center?
   */
  autoSelectHand(side, upperArmAngle, forearmAngle, isConverging = false) {
    // Rule 4: Clap — both hands converging
    if (isConverging) {
      this.setHand(side, 'clap');
      return;
    }

    const globalAngle = upperArmAngle + forearmAngle;

    // Rule 3: Palm-up — elbow sharply bent upward, hand above waist
    // Forearm bent between 60° and 130° AND upper arm is above horizontal
    if (forearmAngle >= 60 && forearmAngle <= 130 && upperArmAngle < 0) {
      this.setHand(side, 'flat-palm-up');
      return;
    }

    // Rule 2: Palm-down — global angle pointing outward/forward (between -45° and 45°)
    if (globalAngle >= -45 && globalAngle <= 45) {
      this.setHand(side, 'flat-palm-down');
      return;
    }

    // Rule 1: Default — open hand (conversational, waving, idle)
    this.setHand(side, 'open');
  }

  /**
   * Rotate the hand from the wrist. Clamped to ±30°.
   * Uses fill-box + transform-origin: center.
   * @param {'left'|'right'} side
   * @param {number} deg
   */
  setHandRotation(side, deg) {
    // No-op — CSS transforms on nested hand groups disconnect them from the forearm
  }

  // ─── Arm Animations ────────────────────────────────────────────────────────

  /**
   * Wave animation — wave one arm then the other, then mexican wave.
   * Left arm: negative = up. Right arm: positive = up.
   */
  async wave(side = 'right', cycles = 3) {
    this.showArms();
    this.setHand('left', 'open');
    this.setHand('right', 'open');

    const otherSide = side === 'left' ? 'right' : 'left';

    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(300);

    await this._waveOneArm(side, cycles);
    await this._delay(250);
    await this._waveOneArm(otherSide, cycles);
    await this._delay(250);

    await this._mexicanWave('left-to-right');
    await this._delay(200);
    await this._mexicanWave('right-to-left');
    await this._delay(200);

    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(400);
    this.hideArms();
  }

  async _waveOneArm(side, cycles) {
    const high = side === 'left' ? -50 : 50;
    const low = side === 'left' ? -15 : 15;

    for (let i = 0; i < cycles; i++) {
      // Up swing: arm rises + elbow bends slightly
      this.setArmRotation(side, high);
      this.setForearmRotation(side, side === 'left' ? -15 : 15);
      await this._delay(380);
      // Down swing: arm lowers + elbow straightens
      this.setArmRotation(side, low);
      this.setForearmRotation(side, side === 'left' ? 8 : -8);
      await this._delay(380);
    }
    // Return to neutral
    this.setArmRotation(side, 0);
    this.setForearmRotation(side, 0);
  }

  async _mexicanWave(direction = 'left-to-right') {
    const step = 150;
    const handStep = 200;
    const first = direction === 'left-to-right' ? 'left' : 'right';
    const second = direction === 'left-to-right' ? 'right' : 'left';
    const firstUp = first === 'left' ? -60 : 60;
    const firstMid = first === 'left' ? -30 : 30;
    const secondUp = second === 'left' ? -60 : 60;
    const secondMid = second === 'left' ? -30 : 30;

    // Prepend: hand flip sequence on the starting hand
    this.setHand(first, 'flat-palm-up');
    await this._delay(handStep);
    this.setHand(first, 'flat-palm-up');
    await this._delay(handStep);
    this.setHand(first, 'open');
    await this._delay(handStep);
    this.setHand(first, 'flat-palm-down-tilt');
    await this._delay(handStep);
    this.setHand(first, 'flat-palm-down');
    await this._delay(handStep);

    // Mexican wave arm motion
    this.setArmRotation(first, 0);
    this.setArmRotation(second, 0);
    await this._delay(step);

    console.log('MEXICAN WAVE: switching to flat-palm-down');
    this.setHand(first, 'flat-palm-down');
    this.setHand(second, 'flat-palm-down');
    this.setArmRotation(first, firstMid);
    await this._delay(step);

    this.setArmRotation(first, firstUp);
    await this._delay(step);

    this.setArmRotation(first, firstMid);
    this.setArmRotation(second, secondMid);
    await this._delay(step);

    this.setArmRotation(first, 0);
    this.setArmRotation(second, secondUp);
    await this._delay(step);

    this.setArmRotation(second, secondMid);
    await this._delay(step);

    this.setArmRotation(second, 0);
    await this._delay(step);

    // Append: reverse hand flip sequence on the ending hand
    this.setHand(second, 'flat-palm-down');
    await this._delay(handStep);
    this.setHand(second, 'flat-palm-down-tilt');
    await this._delay(handStep);
    this.setHand(second, 'open');
    await this._delay(handStep);
    this.setHand(second, 'flat-palm-up');
    await this._delay(handStep);
    this.setHand(second, 'flat-palm-up');
    await this._delay(handStep);

    // Back to hand-open
    this.setHand(first, 'open');
    this.setHand(second, 'open');
  }

  /**
   * Breakdance arm-wave — ripple passes through joints sequentially.
   * Uses shoulder (setArmRotation), elbow (setForearmRotation), wrist (setHandRotation).
   * @param {'left-to-right'|'right-to-left'} [direction]
   */
  async danceWave(direction = 'right-to-left') {
    this.showArms();
    this.setHand('left', 'flat-palm-down');
    this.setHand('right', 'flat-palm-down');

    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(200);

    const step = 130;
    const pop = 20;

    // Do 2 passes
    for (let pass = 0; pass < 2; pass++) {
      if (direction === 'right-to-left') {
        // Right elbow lifts
        this.setForearmRotation('right', pop);
        await this._delay(step);
        // Right shoulder lifts, elbow drops
        this.setForearmRotation('right', 0);
        this.setArmRotation('right', pop);
        await this._delay(step);
        // Cross: right drops, left lifts
        this.setArmRotation('right', 0);
        this.setArmRotation('left', -pop);
        await this._delay(step);
        // Left elbow lifts, shoulder drops
        this.setArmRotation('left', 0);
        this.setForearmRotation('left', -pop);
        await this._delay(step);
        // Left elbow drops
        this.setForearmRotation('left', 0);
        await this._delay(step);
      } else {
        // Left elbow lifts
        this.setForearmRotation('left', -pop);
        await this._delay(step);
        // Left shoulder lifts, elbow drops
        this.setForearmRotation('left', 0);
        this.setArmRotation('left', -pop);
        await this._delay(step);
        // Cross: left drops, right lifts
        this.setArmRotation('left', 0);
        this.setArmRotation('right', pop);
        await this._delay(step);
        // Right elbow lifts, shoulder drops
        this.setArmRotation('right', 0);
        this.setForearmRotation('right', pop);
        await this._delay(step);
        // Right elbow drops
        this.setForearmRotation('right', 0);
        await this._delay(step);
      }
    }

    await this._delay(300);
    this.hideArms();
  }

  /**
   * Child wave — one arm raised, forearm oscillates from elbow
   * (bent up near head ↔ extended outward).
   */
  async childWave(side = 'right', cycles = 4) {
    this.showArms();
    this.setHand('left', 'open');
    this.setHand('right', 'open');

    // Only raise the waving arm — other stays hidden/neutral
    const otherSide = side === 'left' ? 'right' : 'left';
    this._hideLayer(otherSide === 'left' ? 'left-arm' : 'right-arm');

    // Raise the waving arm — negative rotates counterclockwise for right arm
    const armUp = side === 'left' ? -40 : -15;
    this.setArmRotation(side, armUp);
    await this._delay(300);

    // Oscillate forearm from elbow: bent-up ↔ straight (never past 0°)
    const bentUp = side === 'left' ? -45 : 45;
    const straight = side === 'left' ? -5 : 5;

    for (let i = 0; i < cycles; i++) {
      this.setForearmRotation(side, bentUp);
      await this._delay(400);
      this.setForearmRotation(side, straight);
      await this._delay(400);
    }

    // Return to neutral
    this.setForearmRotation(side, 0);
    this.setArmRotation(side, 0);
    await this._delay(300);
    this.hideArms();
  }

  /**
   * Point animation — raise one arm high.
   */
  async point(side = 'right', holdMs = 1500) {
    this.showArms();
    const angle = side === 'left' ? -75 : 75;
    this.setArmRotation(side, angle);
    await this._delay(holdMs);
    this.setArmRotation(side, 0);
    await this._delay(300);
    this.hideArms();
  }

  /**
   * Tada! pose — both arms out in a presenter "ta-da" gesture.
   * Left arm slightly lower, right arm raised high. Hands open.
   * Then slowly lowers over 2 seconds.
   */
  async tada() {
    this.showArms();
    this.setHand('left', 'open');
    this.setHand('right', 'open');

    // Start from slightly higher than final pose
    this.setArmRotation('left', 20);   // left arm out/slightly up
    this.setArmRotation('right', -50);  // right arm raised high
    await this._delay(1500);

    // Slowly lower both arms over 2 seconds
    this.setArmRotation('left', 10);
    this.setArmRotation('right', -30);
    await this._delay(700);

    this.setArmRotation('left', 0);
    this.setArmRotation('right', -15);
    await this._delay(700);

    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(600);
    this.hideArms();
  }

  /**
   * "Welkom by Kassie!" — both arms out with open hands,
   * forearms slowly rotate up 20° over 2 seconds (welcoming gesture).
   */
  async welcome() {
    this.showArms();
    this.setHand('left', 'open');
    this.setHand('right', 'open');

    // Arms out to sides
    this.setArmRotation('left', -15);
    this.setArmRotation('right', 15);
    await this._delay(400);

    // Slowly rotate forearms up over 2 seconds (4 steps × 500ms)
    this.setForearmRotation('left', -5);
    this.setForearmRotation('right', 5);
    await this._delay(500);

    this.setForearmRotation('left', -10);
    this.setForearmRotation('right', 10);
    await this._delay(500);

    this.setForearmRotation('left', -15);
    this.setForearmRotation('right', 15);
    await this._delay(500);

    this.setForearmRotation('left', -20);
    this.setForearmRotation('right', 20);
    await this._delay(500);

    // Hold the welcoming pose
    await this._delay(1500);

    // Return
    this.setForearmRotation('left', 0);
    this.setForearmRotation('right', 0);
    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(400);
    this.hideArms();
  }

  /**
   * "Ek weet nie" / shrug — both arms out palms up, slight shoulder lift.
   * Hands flat (palms up).
   */
  async shrug(holdMs = 2000) {
    this.showArms();
    this.setHand('left', 'flat-palm-up');
    this.setHand('right', 'flat-palm-up');

    // Both arms out to sides, slightly raised
    this.setArmRotation('left', -20);
    this.setArmRotation('right', 20);
    // Forearms angled up slightly (palms-up look)
    this.setForearmRotation('left', -15);
    this.setForearmRotation('right', 15);
    await this._delay(holdMs);

    // Return to neutral
    this.setForearmRotation('left', 0);
    this.setForearmRotation('right', 0);
    this.setArmRotation('left', 0);
    this.setArmRotation('right', 0);
    await this._delay(300);
    this.hideArms();
  }

  // ─── Compound Expressions ──────────────────────────────────────────────────

  setExpression(name) {
    switch (name) {
      case 'happy':
        this.setMouth('smile-1');
        this.setEars('up');
        this.setEyebrows('neutral');
        break;
      case 'excited':
        this.setMouth('BigSmile');
        this.setEars('up');
        this.setEyebrows('raised');
        break;
      case 'surprised':
        this.setMouth('ooh');
        this.setEars('up');
        this.setEyebrows('raised');
        break;
      case 'thinking':
        this.setMouth('m');
        this.setEars('down');
        this.setEyebrows('furrowed');
        break;
      case 'talking':
        this.setMouth('default');
        this.setEars('up');
        this.setEyebrows('neutral');
        break;
      case 'singing':
        this.setMouth('ooh');
        this.setEars('up');
        this.setEyebrows('raised');
        break;
      case 'neutral':
      default:
        this.setMouth('smile-1');
        this.setEars('up');
        this.setEyebrows('neutral');
        break;
    }
  }

  // ─── Speaking ──────────────────────────────────────────────────────────────

  /**
   * Animate the mouth while a line is being "spoken" for durationMs.
   *
   * When text is provided, the mouth changes once per word (not on a fixed
   * timer) — each word gets a shape chosen from its first letter (see
   * WORD_START_MOUTH/setMouthForWord()), held for a share of durationMs
   * proportional to the number of words in the line. Without text (or for
   * text with no words), falls back to the old random flap-every-280ms
   * behavior so callers that only ever passed a duration still work.
   * @param {string|number} textOrDurationMs - the line being spoken, or (legacy) a duration in ms
   * @param {number} [durationMs] - total time to animate for, when text is passed as the first argument
   */
  speak(textOrDurationMs, durationMs) {
    // Legacy call shape: speak(durationMs) with no text.
    const text = typeof textOrDurationMs === 'string' ? textOrDurationMs : '';
    const totalMs = typeof textOrDurationMs === 'string' ? durationMs : textOrDurationMs;

    return new Promise((resolve) => {
      const words = text.split(/\s+/).filter(Boolean);

      if (words.length === 0) {
        // No word to anchor a shape to — keep the previous random-flap feel.
        const flapInterval = 280; // Kid-friendly pace
        let elapsed = 0;
        const flap = () => {
          if (elapsed >= totalMs) {
            this.setMouth('smile-1');
            resolve();
            return;
          }
          this.talkFrame();
          elapsed += flapInterval;
          this.timers.talk = setTimeout(flap, flapInterval);
        };
        flap();
        return;
      }

      // One mouth change per word, each held for an even share of totalMs
      // (clamped so a very long line doesn't hold each word for too short
      // a flash, and a one-word line doesn't hold it for the full duration
      // without any movement).
      const perWordMs = Math.min(900, Math.max(180, totalMs / words.length));
      let idx = 0;
      const advance = () => {
        if (idx >= words.length) {
          this.setMouth('smile-1');
          resolve();
          return;
        }
        this.setMouthForWord(words[idx]);
        idx += 1;
        this.timers.talk = setTimeout(advance, perWordMs);
      };
      advance();
    });
  }

  stopSpeaking() {
    clearTimeout(this.timers.talk);
    this.setMouth('smile-1');
  }

  // ─── Mouth Cycling (idle animation) ────────────────────────────────────────

  startMouthCycle() {
    const sequence = ['smile-1', 'default', 'ooh', 'smile-2', 'default'];
    let idx = 0;

    const cycle = () => {
      idx = (idx + 1) % sequence.length;
      this.setMouth(sequence[idx]);
      const delay = CONFIG.mouthCycleMin + Math.random() * (CONFIG.mouthCycleMax - CONFIG.mouthCycleMin);
      this.timers.mouthCycle = setTimeout(cycle, delay);
    };

    const delay = CONFIG.mouthCycleMin + Math.random() * (CONFIG.mouthCycleMax - CONFIG.mouthCycleMin);
    this.timers.mouthCycle = setTimeout(cycle, delay);
  }

  stopMouthCycle() {
    clearTimeout(this.timers.mouthCycle);
  }

  // ─── Utilities ─────────────────────────────────────────────────────────────

  /**
   * Looking around sequence — eyes move to random positions as if searching.
   * Used on mobile (no mouse) or when no mouse activity for 5+ seconds.
   */
  async lookAround() {
    if (this.blinking || !this.svg) return;
    const positions = [
      { x: -1.0, y: -0.8 },  // upper-left
      { x: 1.0, y: -0.5 },   // upper-right
      { x: -0.5, y: 0.5 },   // lower-left
      { x: 0.8, y: 0.0 },    // right
      { x: -1.0, y: 0.3 },   // left-down
      { x: 0.0, y: -1.0 },   // up
      { x: 0.5, y: 0.8 },    // lower-right
      { x: 0.0, y: 0.0 },    // center
    ];

    const leftPupil = this.layers['left-pupil'];
    const rightPupil = this.layers['right-pupil'];

    // Pick 3-4 random positions to look at
    const count = 3 + Math.floor(Math.random() * 2);
    for (let i = 0; i < count; i++) {
      const pos = positions[Math.floor(Math.random() * positions.length)];
      if (leftPupil) leftPupil.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      if (rightPupil) rightPupil.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
      await this._delay(600 + Math.random() * 800);
    }

    // Return to center
    if (leftPupil) leftPupil.style.transform = '';
    if (rightPupil) rightPupil.style.transform = '';
  }

  _delay(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  destroy() {
    Object.values(this.timers).forEach(t => clearTimeout(t));
    this.timers = {};
    this.stopBlinking();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.initialized = false;
  }
}
